import { post, put, del, get } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR from 'swr'
import { mutate } from "swr"
import { useContext } from 'react';
import UserProfileContext from '../context/userprofilecontext.js';


async function getDRFAIncomeExpense() {


  const payload = {
    apiName: 'LivisiblyDRFAIncomeExpenseApi',
    path: 'drfa/income_expense'

  }

  const restOperation = get(payload);


  const response = await restOperation.response;
  const result = processResponse(response)
  // console.log(`Result: ${JSON.stringify(result)})`);
  return result
}


// The report is case data: its cache key carries the caseID so a case switch
// never shows the previous case's DRFA (dashboard-card rule).
function useDRFAReportKey() {
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  return caseID ? ['DRFA_INCOME_EXPENSE', caseID] : null;
}

export function useGetDRFAIncomeExpense(shouldFetch = true) {
  const key = useDRFAReportKey();
  const result = useSWR(
    shouldFetch ? key : null,
    () => getDRFAIncomeExpense()
  );
  return result;
}

async function updateDRFAIncomeExpense(drfaCategoryName,  newStatedAmount) {
  // console.log(`entered updateDRFAIncomeExpense drfaData:`)
  // console.log(JSON.stringify(drfaData, null, 2))
  const payload = {
    apiName: 'LivisiblyDRFAIncomeExpenseApi',
    path: 'drfa/income_expense',
    options: {
      body: {
        drfa_category_name: drfaCategoryName,
        new_stated_amount: newStatedAmount
      }
    }
  }
  // console.log(`posting payload: ${JSON.stringify(payload)}`)
  const restOperation = post(payload);


  const response = await restOperation.response;
  const result = processResponse(response)
  // console.log(`Result: ${JSON.stringify(result)})`);
  return result
}

export function useUpdateDRFAIncomeExpense() {
  const key = useDRFAReportKey();
  return (drfaItemType, drfaCategoryName, newStatedAmount, optimisticData) => {
    if (!key) return;
    const options={
      optimisticData: optimisticData, // Optimistically update the cache
      revalidate: true,  // Don't revalidate immediately because the mutation will return the updated data
    }
    mutate(key,
      updateDRFAIncomeExpense(drfaCategoryName,  newStatedAmount) // Call the update function
      , options);
  };
}

// Drill-down: live query that returns transaction IDs aggregating into a
// DRFA category's monthly average. Used by the Sankey/Transaction Analysis
// page after the user clicks a count in the DRFA report — the DRFA hierarchy
// doesn't share names with the Livisibly category/subcategory hierarchy, so
// we resolve the underlying transactions on the server (via MAPS_TO).
async function getDRFADrilldownTransactionIDs(drfaCategoryName) {
  const queryString = new URLSearchParams({ drfa_category_name: drfaCategoryName }).toString();
  const payload = {
    apiName: 'LivisiblyDRFAIncomeExpenseApi',
    path: `drfa/drilldown?${queryString}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetDRFADrilldownTransactionIDs(drfaCategoryName, shouldFetch = true) {
  // Scope cache by caseID — same reasoning as the transactions hooks: a case
  // switch must invalidate cached IDs, not silently leak from the prior case.
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID && drfaCategoryName
      ? ['DRFA_DRILLDOWN', caseID, drfaCategoryName]
      : null,
    () => getDRFADrilldownTransactionIDs(drfaCategoryName)
  );
  return result;
}

// Drill-in for a MEASURED self-employment line (one per business account):
// the server applies the same account test and row predicate as the line, so
// the list matches the line's transaction count exactly.
async function getDRFABusinessDrilldownTransactionIDs(businessAccountId) {
  const queryString = new URLSearchParams({ business_account_id: businessAccountId }).toString();
  const payload = {
    apiName: 'LivisiblyDRFAIncomeExpenseApi',
    path: `drfa/drilldown?${queryString}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

// Drill-in for the "Business activity in personal accounts" measured line:
// BUSINESS-chart income and expenses on personal accounts, resolved server-side
// with the same account test and row predicate as the line.
async function getDRFACrossLedgerDrilldownTransactionIDs(crossLedger) {
  const queryString = new URLSearchParams({ cross_ledger: crossLedger }).toString();
  const payload = {
    apiName: 'LivisiblyDRFAIncomeExpenseApi',
    path: `drfa/drilldown?${queryString}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetDRFACrossLedgerDrilldownTransactionIDs(crossLedger, shouldFetch = true) {
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  return useSWR(
    shouldFetch && caseID && crossLedger
      ? ['DRFA_CROSS_LEDGER_DRILLDOWN', caseID, crossLedger]
      : null,
    () => getDRFACrossLedgerDrilldownTransactionIDs(crossLedger)
  );
}

export function useGetDRFABusinessDrilldownTransactionIDs(businessAccountId, shouldFetch = true) {
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  return useSWR(
    shouldFetch && caseID && businessAccountId
      ? ['DRFA_BUSINESS_DRILLDOWN', caseID, businessAccountId]
      : null,
    () => getDRFABusinessDrilldownTransactionIDs(businessAccountId)
  );
}
